import { computed, defineComponent, onMounted, PropType, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useSweetAlert } from '@/composables/sweetalert';
import { RegionGetter } from '@/store/region/region.module';
import { Region } from '@/store/region/region.model';
import { ProvinceAction, ProvinceGetter } from '@/store/province/province.module';
import { Province } from '@/store/province/province.model';
import { AppointmentRegionAction, AppointmentRegionGetter } from '@/store/appointmentRegion/appointmentRegion.module';
import { AppointmentRegion } from '@/store/appointmentRegion/appointmentRegion.model';
import { AppointmentRegionSchema } from '@/store/appointmentRegion/appointmentRegion.validator';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';
import Multiselect from '@vueform/multiselect';
import _ from 'lodash';

export default defineComponent({
  name: 'AppointmentRegionFormComponent',
  components: {
    BaseModalComponent,
    Multiselect
  },
  props: {
    appointmentRegion: {
      type: Object as PropType<{
        appointmentId: number,
        regionId?: number | null,
        provinces: string[]
      }>,
      required: true,
    },
  },
  emits: [
    'clear'
  ],
  setup: (props, context) => {
    const store = useStore();
    const swal = useSweetAlert();
    const isEditMode = computed(() => props.appointmentRegion?.provinces.length !== 0);
    const title = computed(() => isEditMode.value ? 'แก้ไขข้อมูลเขตตรวจราชการในหนังสือคำสั่ง' : 'เพิ่มข้อมูลเขตตรวจราชการในหนังสือคำสั่ง');
    const appointmentRegions = computed<AppointmentRegion[]>(() => store.getters[AppointmentRegionGetter.ALL]);
    const selectedProvinces = computed<string[]>(() => _.chain(appointmentRegions.value).map(m => m.provinces).flatten()
      .filter(m => props.appointmentRegion?.provinces.every(p => p !== m.name)).map(p => p.name).value());
    const provinces = computed<Province[]>(() => {
      const all = store.getters[ProvinceGetter.ALL] as Province[];
      return all.filter(m => m.iso !== 'TH-10');
    });
    const provinceOptions = computed<string[]>(() => _.chain(provinces.value).filter(m => selectedProvinces.value.every(x => m.name !== x)).map(p => p.name).value());
    const region = computed<Region | undefined>(() => store.getters[RegionGetter.BY_ID](props.appointmentRegion?.regionId));
    const appointmentRegionForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      appointmentId: number;
      regionId?: number | null;
      provinces: string[];
    }
    const formData = reactive<Form>({
      appointmentId: props.appointmentRegion?.appointmentId,
      regionId: null,
      provinces: []
    });
    const validator = computed(() => AppointmentRegionSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      provinces: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'provinces')),
    });
    const isValid = computed(() => !errors.provinces);
    const isLoading = computed(() => store.getters[AppointmentRegionGetter.STATUS] === 'init' || store.getters[AppointmentRegionGetter.STATUS] === 'loading');
    const submited = ref(false);

    onMounted(() => {
      store.dispatch(ProvinceAction.FETCH);
    });

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.regionId = props.appointmentRegion?.regionId;
        if (isEditMode.value) {
          formData.provinces = props.appointmentRegion?.provinces;
        }
        resolve();
      })
      .then(() => appointmentRegionForm.value?.show());
    };

    // hide modal
    const hide = () => {
      appointmentRegionForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      context.emit('clear');
      new Promise<void>((resolve) => {
        formData.regionId = null,
        formData.provinces = [],
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(AppointmentRegionAction.UPDATE, formData)
            .then(() => hide());
        } else {
          store.dispatch(AppointmentRegionAction.CREATE, formData)
            .then(() => hide());
        }

        clearForm();
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(AppointmentRegionAction.DELETE, formData)
              .then(() => hide());
          }
        });
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      provinceOptions,
      region,
      appointmentRegionForm,
      show,
      hide,
      unsubmit,
      clearForm,
      submitClick,
      deleteClick,
    };
  },
});
